import type { ReactNode } from "react";

export interface Option {
  label: string;
  value: string;
}

export interface PatientStatePayload {
  selectedClassification: string;
  options: Option[];
  evidenceByClassification: Record<string, string>;
  linkLabel: string;
  linkPath: string;
}

export interface PaymentClassificationPayload {
  selectedClassification?: string | null;
  options: Option[];
  evidenceByClassification: Record<string, string>;
  linkLabel: string;
  linkPath: string;
}

export interface BaseInviteFormState {
  recipientEmail: string;
  expiresAt: string;
  emailSubject: string;
  emailBody: string;
  patientStateClassification: string;
}

export interface ExistingAppointment {
  scheduleLabel: string;
  locationLabel: string;
  timing: "past" | "today" | "future";
  path: string;
}

export interface WorkspaceBase<FormState extends BaseInviteFormState> {
  authenticityToken: string;
  submitUrl: string;
  submitMethod: "POST" | "PATCH";
  submitLabel: string;
  editing: boolean;
  editNotice?: string | null;
  previewUrl: string;
  availabilityUrl: string;
  cancelPath: string;
  linkPlaceholder: string;
  errors: string[];
  outstandingInviteWarning?: string | null;
  outstandingInviteEditPath?: string | null;
  existingAppointments: ExistingAppointment[];
  patientState: PatientStatePayload;
  form: FormState;
}

export interface FlashMessage {
  tone: "success" | "error" | "warning";
  message: string;
}

export function flashClasses(tone: FlashMessage["tone"]) {
  switch (tone) {
    case "success":
      return "app-alert-success";
    case "warning":
      return "app-alert-warning";
    default:
      return "app-alert-error";
  }
}

export function openSchedulingPreview(previewUrl: string, authenticityToken: string, payload: Record<string, unknown>) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = previewUrl;
  form.target = "_blank";
  // Keep the opener isolated, but preserve the request origin so Rails can verify the CSRF-protected POST.
  form.setAttribute("rel", "noopener");

  const addField = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  addField("authenticity_token", authenticityToken);
  addField("preview_payload", JSON.stringify(payload));
  document.body.appendChild(form);
  form.submit();
  form.remove();
}

export function AlertBanner({ flash }: { flash: FlashMessage | null }) {
  if (!flash?.message) return null;

  return <div className={flashClasses(flash.tone)}>{flash.message}</div>;
}

export function ErrorList({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;

  return <AlertBanner flash={{ tone: "error", message: errors.join(", ") }} />;
}

export function ExistingAppointmentsNotice({
  appointments,
  appointmentType,
}: {
  appointments: ExistingAppointment[];
  appointmentType: "consult" | "operation";
}) {
  if (appointments.length === 0) return null;

  const pluralizedType = appointments.length === 1 ? appointmentType : `${appointmentType}s`;

  return (
    <section className="app-alert-warning" aria-label={`Existing ${pluralizedType}`}>
      <p className="font-semibold">
        This patient already has {appointments.length === 1 ? "a" : appointments.length} scheduled {pluralizedType} on record.
      </p>
      <ul className="mt-2 space-y-1.5">
        {appointments.map((appointment) => (
          <li key={appointment.path} className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="app-badge border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              {appointment.timing === "today" ? "Today" : appointment.timing === "future" ? "Future" : "Past"}
            </span>
            <a href={appointment.path} className="font-semibold underline decoration-dotted underline-offset-2">
              {appointment.scheduleLabel}
            </a>
            <span>• {appointment.locationLabel}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PatientStateSection({
  patientState,
  value,
  onChange,
}: {
  patientState: PatientStatePayload;
  value: string;
  onChange: (value: string) => void;
}) {
  const evidenceText = patientState.evidenceByClassification[value] || "";

  return (
    <div className="border-b border-neutral-200/80 pb-4 dark:border-neutral-800">
      <div className="grid gap-3 md:grid-cols-[minmax(0,18rem)_1fr] md:items-start">
        <div>
          <label htmlFor="scheduling-invite-patient-state" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Patient location
          </label>
          <select
            id="scheduling-invite-patient-state"
            className="app-select mt-1"
            required
            value={value}
            onChange={(event) => onChange(event.target.value)}
          >
            {patientState.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Source</p>
          <div className="mt-1 text-sm leading-6 text-neutral-600 dark:text-neutral-300">
            <p>
              {evidenceText}{" "}
              <a
                href={patientState.linkPath}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
              >
                {patientState.linkLabel}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PaymentClassificationSection({
  payment,
  value,
  onChange,
}: {
  payment: PaymentClassificationPayload;
  value: string;
  onChange: (value: string) => void;
}) {
  const evidenceText = payment.evidenceByClassification[value] || "Choose the payment type for this invite.";

  return (
    <div className="pt-4">
      <div className="grid gap-3 md:grid-cols-[minmax(0,18rem)_1fr] md:items-start">
        <div>
          <label htmlFor="consult-scheduling-payment-classification" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Payment type
          </label>
          <select
            id="consult-scheduling-payment-classification"
            className="app-select mt-1"
            required
            value={value}
            onChange={(event) => onChange(event.target.value)}
          >
            <option value="">Choose payment type</option>
            {payment.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Source</p>
          <div className="mt-1 text-sm leading-6 text-neutral-600 dark:text-neutral-300">
            <p>
              {evidenceText}{" "}
              <a
                href={payment.linkPath}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 hover:text-emerald-800 dark:text-emerald-300 dark:hover:text-emerald-200"
              >
                {payment.linkLabel}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EmailDetailsSection<FormState extends BaseInviteFormState>({
  form,
  linkPlaceholder,
  onChange,
  showHeading = true,
}: {
  form: FormState;
  linkPlaceholder: string;
  onChange: <Key extends keyof FormState>(field: Key, value: FormState[Key]) => void;
  showHeading?: boolean;
}) {
  return (
    <>
      {showHeading ? (
        <div className="mt-4 flex flex-col gap-2">
          <div>
            <p className="app-kicker">Email</p>
            <h2 className="mt-1 text-lg font-semibold text-neutral-950 dark:text-white">Invite details</h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            The email body can include <code>{linkPlaceholder}</code>. If you leave it out, the scheduling link is appended automatically.
          </p>
        </div>
      ) : null}

      <div className={`${showHeading ? "mt-4" : ""} grid gap-4 md:grid-cols-2`}>
        <div>
          <label htmlFor="scheduling-invite-recipient-email" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Recipient email
          </label>
          <input
            id="scheduling-invite-recipient-email"
            type="email"
            className="app-input"
            required
            autoComplete="email"
            value={form.recipientEmail}
            onChange={(event) => onChange("recipientEmail", event.target.value as FormState["recipientEmail"])}
          />
        </div>

        <div>
          <label htmlFor="scheduling-invite-expires-at" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Link expires at
          </label>
          <input
            id="scheduling-invite-expires-at"
            type="datetime-local"
            className="app-input"
            required
            value={form.expiresAt}
            onChange={(event) => onChange("expiresAt", event.target.value as FormState["expiresAt"])}
          />
        </div>

        <div>
          <label htmlFor="scheduling-invite-email-subject" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Email subject
          </label>
          <input
            id="scheduling-invite-email-subject"
            type="text"
            className="app-input"
            required
            value={form.emailSubject}
            onChange={(event) => onChange("emailSubject", event.target.value as FormState["emailSubject"])}
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="scheduling-invite-email-body" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Email body
          </label>
          <textarea
            id="scheduling-invite-email-body"
            rows={9}
            className="app-input min-h-48"
            required
            value={form.emailBody}
            onChange={(event) => onChange("emailBody", event.target.value as FormState["emailBody"])}
          />
        </div>
      </div>
    </>
  );
}

export function PillGroup({
  label,
  helpText,
  options,
  value,
  onChange,
}: {
  label: string;
  helpText?: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
}) {
  const showHeader = Boolean(label || helpText);

  return (
    <div className="mt-4">
      {showHeader ? (
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{label}</p>
          {helpText ? (
            <span className="group relative inline-flex" tabIndex={0} aria-label={helpText}>
              <span className="flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-neutral-300 text-xs font-semibold text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-neutral-600 dark:hover:text-neutral-200">
                ?
              </span>
              <span
                role="tooltip"
                className="pointer-events-none absolute left-0 top-7 z-10 w-72 rounded-lg border border-neutral-200 bg-white p-3 text-sm font-normal leading-5 text-neutral-600 opacity-0 shadow-lg transition group-hover:opacity-100 group-focus-within:opacity-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300"
              >
                {helpText}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={`${showHeader ? "mt-2" : ""} flex flex-wrap gap-2`}>
        {options.map((option) => {
          const active = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              className={`app-pill cursor-pointer ${active ? "app-pill-active" : "app-pill-inactive"}`}
              aria-pressed={active}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function AvailabilityPager({
  rangeLabel,
  previousStartDate,
  nextStartDate,
  loading,
  onPage,
}: {
  rangeLabel: string;
  previousStartDate?: string | null;
  nextStartDate: string;
  loading: boolean;
  onPage: (startDate: string) => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{rangeLabel}</p>
      <div className="flex flex-wrap gap-2">
        {previousStartDate ? (
          <button type="button" className="app-button-secondary" disabled={loading} onClick={() => onPage(previousStartDate)}>
            Previous
          </button>
        ) : (
          <span className="app-button-secondary cursor-not-allowed opacity-50" aria-disabled="true">
            Previous
          </span>
        )}
        <button type="button" className="app-button-secondary" disabled={loading} onClick={() => onPage(nextStartDate)}>
          Next
        </button>
      </div>
    </div>
  );
}

export function SectionIntro({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <p className="app-kicker">{kicker}</p>
        <h2 className="mt-1 text-lg font-semibold text-neutral-950 dark:text-white">{title}</h2>
      </div>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{children}</p>
    </div>
  );
}

export function responseErrorMessage(payload: { errors?: string[] }, fallback: string) {
  return Array.isArray(payload.errors) && payload.errors.length > 0 ? payload.errors.join(", ") : fallback;
}
